import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  AlertTriangle, Search, Send, FileText,
  CheckCircle, XCircle, MessageSquare, ChevronDown, ChevronUp, RefreshCw, Gift,
} from 'lucide-react';
import complaintService from '../../services/complaintService';
import { formatIstDateTime } from '../../lib/constants';

const IMAGE_REQUIRED_TYPES = new Set(['Product Damaged', 'Wrong Item Delivered', 'Poor Quality', 'Packaging Issue']);

const complaintSuggestions = [
  { value: 'Product Damaged', label: 'Product Damaged', hint: 'The item arrived broken, leaking, or unusable.' },
  { value: 'Wrong Item Delivered', label: 'Wrong Item Delivered', hint: 'You received a different product than ordered.' },
  { value: 'Missing Item', label: 'Missing Item', hint: 'Part of your order is missing or skipped.' },
  { value: 'Late Delivery', label: 'Late Delivery', hint: 'The order arrived later than expected.' },
  { value: 'Poor Quality', label: 'Poor Quality', hint: 'The product quality does not match the description.' },
  { value: 'Packaging Issue', label: 'Packaging Issue', hint: 'The item was poorly packed or leaked during transit.' },
  { value: 'Other', label: 'Other', hint: 'Describe the issue in your own words.' },
];

const statusConfig = {
  open:      { icon: AlertTriangle, color: 'bg-orange-100 text-orange-700', label: 'OPEN' },
  in_review: { icon: Search,        color: 'bg-yellow-100 text-yellow-700', label: 'IN REVIEW' },
  resolved:  { icon: CheckCircle,   color: 'bg-green-100 text-green-700',   label: 'RESOLVED' },
  closed:    { icon: XCircle,       color: 'bg-gray-100 text-gray-700',     label: 'CLOSED' },
};

const ReplyBubble = ({ reply }) => {
  if (reply.senderType === 'system') {
    return (
      <div className="flex justify-center my-2">
        <div className="inline-flex items-center gap-2.5 px-4 py-2.5 bg-amber-50 border border-amber-200 rounded-2xl shadow-sm text-xs font-medium text-amber-900 max-w-[90%]">
          <div className="w-6 h-6 rounded-full bg-amber-500 text-white flex items-center justify-center shrink-0 shadow-xs">
            <Gift className="w-3.5 h-3.5" />
          </div>
          <span className="font-semibold">{reply.message}</span>
          <span className="text-amber-500 shrink-0 text-[11px]">
            · {new Date(reply.createdAt).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', hour12: true })}
          </span>
        </div>
      </div>
    );
  }
  const isAdmin = reply.senderType === 'admin';
  const bubbleClass = isAdmin ? 'bg-[#2e4e18] text-white' : 'bg-[#f2f8eb] border border-[#c8e6a0] text-gray-800';
  const nameClass = isAdmin ? 'text-green-200' : 'text-[#2e4e18]';
  const timeClass = isAdmin ? 'text-green-300' : 'text-gray-400';
  return (
    <div className={`flex ${isAdmin ? 'justify-start' : 'justify-end'}`}>
      <div className={`max-w-[85%] rounded-2xl px-4 py-3 ${bubbleClass}`}>
        <p className={`text-[10px] font-semibold mb-1 ${nameClass}`}>{reply.senderName}</p>
        <p className="text-sm">{reply.message}</p>
        <p className={`text-[10px] mt-1 ${timeClass}`}>
          {new Date(reply.createdAt).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', hour12: true })}
        </p>
      </div>
    </div>
  );
};

ReplyBubble.propTypes = {
  reply: PropTypes.shape({
    id: PropTypes.number.isRequired,
    senderType: PropTypes.string.isRequired,
    senderName: PropTypes.string.isRequired,
    message: PropTypes.string.isRequired,
    createdAt: PropTypes.string.isRequired,
  }).isRequired,
};

const GuestComplaintCard = ({ complaint, orderNumber, guestPhone }) => {
  const hasAdminReply =
    complaint.replies?.some((r) => r.senderType === 'admin') || !!complaint.adminComment;
  const [expanded, setExpanded] = useState(hasAdminReply);
  const [replies, setReplies] = useState(complaint.replies || []);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);

  // Sync replies when parent re-fetches server data
  const serverReplyIds = (complaint.replies || []).map((r) => r.id).join(',');
  useEffect(() => {
    setReplies((prev) => {
      const serverReplies = complaint.replies || [];
      const serverIdSet = new Set(serverReplies.map((r) => r.id));
      const localOnly = prev.filter((r) => !serverIdSet.has(r.id));
      return [...serverReplies, ...localOnly];
    });
  }, [serverReplyIds]);

  const cfg = statusConfig[complaint.status] || statusConfig.open;
  const StatusIcon = cfg.icon;

  const handleSendReply = async () => {
    if (!replyText.trim()) return;
    setSending(true);
    const result = await complaintService.addGuestReply(
      complaint.id, orderNumber, guestPhone, replyText.trim(),
    );
    if (result.success) {
      setReplies((prev) => [...prev, result.data]);
      setReplyText('');
    }
    setSending(false);
  };

  let toggleLabel = 'Add a reply';
  if (expanded) toggleLabel = 'Hide conversation';
  else if (hasAdminReply) toggleLabel = `View admin reply & respond (${replies.length})`;
  const ChevronIcon = expanded ? ChevronUp : ChevronDown;

  return (
    <div className={`rounded-3xl border p-4 ${hasAdminReply ? 'border-[#548B2E] bg-[#f8fcf3]' : 'border-gray-200 bg-white'}`}>
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="font-semibold text-gray-800">{complaint.issueType}</p>
          <p className="text-xs text-gray-500 mt-0.5">
            {formatIstDateTime(complaint.createdAt)}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {hasAdminReply && (
            <span className="inline-flex items-center gap-1 rounded-full bg-[#2e4e18] text-white text-[11px] font-semibold px-3 py-1">
              <MessageSquare className="w-3 h-3" /> Admin replied
            </span>
          )}
          <span className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold ${cfg.color}`}>
            <StatusIcon className="w-3 h-3" />
            {cfg.label}
          </span>
        </div>
      </div>

      {/* Description */}
      <p className="mt-3 text-sm text-gray-700">{complaint.description}</p>

      {/* Images */}
      {complaint.images?.length > 0 && (
        <div className="mt-3 grid grid-cols-2 gap-2">
          {complaint.images.map((src) => (
            <img key={src} src={src} alt="complaint" className="h-24 w-full object-cover rounded-2xl border border-gray-200" />
          ))}
        </div>
      )}

      {/* Thread toggle */}
      <button
        type="button"
        onClick={() => setExpanded((prev) => !prev)}
        className="mt-4 flex items-center gap-2 text-xs text-[#548B2E] font-medium hover:underline"
      >
        <MessageSquare className="w-3.5 h-3.5" />
        {toggleLabel}
        <ChevronIcon className="w-3.5 h-3.5" />
      </button>

      {/* Thread panel */}
      {expanded && (
        <div className="mt-3 rounded-2xl border border-gray-200 bg-white p-3 space-y-3">
          {/* Legacy adminComment (old one-way system) */}
          {complaint.adminComment && replies.length === 0 && (
            <div className="flex justify-start">
              <div className="max-w-[85%] rounded-2xl px-4 py-3 bg-[#2e4e18] text-white">
                <p className="text-[10px] font-semibold mb-1 text-green-200">Admin Team</p>
                <p className="text-sm">{complaint.adminComment}</p>
              </div>
            </div>
          )}

          {/* Structured replies */}
          {replies.map((reply) => <ReplyBubble key={reply.id} reply={reply} />)}

          {replies.length === 0 && !complaint.adminComment && (
            <p className="text-center text-xs text-gray-400 py-3">No replies yet — admin will respond here soon.</p>
          )}

          {/* Guest reply input */}
          <div className="flex gap-2 pt-1">
            <input
              type="text"
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSendReply()}
              placeholder="Reply to this complaint..."
              className="flex-1 border border-gray-200 rounded-2xl px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#548B2E] bg-white"
            />
            <button
              onClick={handleSendReply}
              disabled={sending || !replyText.trim()}
              className="bg-[#2e4e18] text-white px-3 py-2 rounded-2xl hover:bg-[#3f6b20] transition-colors disabled:opacity-50"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

GuestComplaintCard.propTypes = {
  orderNumber: PropTypes.string.isRequired,
  guestPhone: PropTypes.string.isRequired,
  complaint: PropTypes.shape({
    id: PropTypes.number.isRequired,
    issueType: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
    createdAt: PropTypes.string.isRequired,
    adminComment: PropTypes.string,
    images: PropTypes.arrayOf(PropTypes.string),
    replies: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number.isRequired,
        senderType: PropTypes.string.isRequired,
        senderName: PropTypes.string.isRequired,
        message: PropTypes.string.isRequired,
        createdAt: PropTypes.string.isRequired,
      }),
    ),
  }).isRequired,
};

const GuestComplaints = () => {
  const [step, setStep] = useState('lookup');
  const [orderNumber, setOrderNumber] = useState('');
  const [guestPhone, setGuestPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [complaints, setComplaints] = useState([]);

  const [issueType, setIssueType] = useState('Product Damaged');
  const [description, setDescription] = useState('');
  const [images, setImages] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState(null);

  // Silent background fetch — updates complaints without showing a loading spinner
  const silentFetch = async (on, gp) => {
    const result = await complaintService.getGuestComplaints(on, gp);
    if (result.success) setComplaints(result.data.complaints || []);
  };

  const handleManualRefresh = async () => {
    setRefreshing(true);
    await silentFetch(orderNumber, guestPhone);
    setRefreshing(false);
  };

  // Real-time updates via SSE — only active while on the complaints step
  useEffect(() => {
    if (step !== 'complaints') return;
    const apiUrl = process.env.REACT_APP_API_URL || 'https://bookmyfresh.com/api';
    const es = new EventSource(`${apiUrl}/complaints/stream`);
    es.addEventListener('update', () => silentFetch(orderNumber, guestPhone));
    return () => es.close();
  }, [step]); // orderNumber and guestPhone are stable once the lookup succeeds

  const handleLookup = async () => {
    if (!orderNumber.trim() || !guestPhone.trim()) {
      setMessage('Please enter both Order ID and Mobile Number');
      return;
    }
    const digits = guestPhone.trim().replace(/\D/g, '').slice(-10);
    if (digits.length !== 10) {
      setMessage('Please enter a valid 10-digit mobile number');
      return;
    }
    setLoading(true);
    setMessage(null);
    try {
      const result = await complaintService.getGuestComplaints(orderNumber.trim(), digits);
      if (result.success) {
        setGuestPhone(digits); // store normalized form for all subsequent calls
        setComplaints(result.data.complaints || []);
        setStep('complaints');
      } else {
        setMessage(result.message || 'Order not found or phone number does not match');
      }
    } catch {
      setMessage('Invalid order details. Please check and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (event) => {
    const selected = Array.from(event.target.files || []).filter((f) => f.type.startsWith('image/')).slice(0, 4);
    setImages(selected);
    setPreviews(selected.map((f) => URL.createObjectURL(f)));
  };

  const afterSubmitSuccess = async () => {
    setMessage('Complaint submitted successfully! Our team will review it soon.');
    setDescription('');
    setImages([]);
    setPreviews([]);
    const refreshed = await complaintService.getGuestComplaints(orderNumber, guestPhone);
    if (refreshed.success) setComplaints(refreshed.data.complaints || []);
    setStep('complaints');
  };

  const handleSubmitComplaint = async () => {
    if (!description.trim()) {
      setMessage('Please describe your complaint so our team can help.');
      return;
    }
    if (IMAGE_REQUIRED_TYPES.has(issueType) && images.length === 0) {
      setMessage('Please attach at least one image — it is required for this type of complaint.');
      return;
    }
    setSubmitting(true);
    setMessage(null);
    const formData = new FormData();
    formData.append('issueType', issueType);
    formData.append('description', description.trim());
    images.forEach((img) => formData.append('images', img));

    const result = await complaintService.submitGuestComplaint(orderNumber, guestPhone, formData);
    if (result.success) {
      await afterSubmitSuccess();
    } else {
      setMessage(result.message || 'Failed to submit complaint');
    }
    setSubmitting(false);
  };

  /* ── LOOKUP STEP ─────────────────────────────────────────────── */
  if (step === 'lookup') {
    return (
      <div className="min-h-screen bg-gray-50 pt-24 pb-16 px-4">
        <div className="max-w-md mx-auto bg-white rounded-3xl shadow-sm p-6">
          <div className="text-center mb-6">
            <div className="w-14 h-14 rounded-3xl bg-[#F8F4E8] flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="w-7 h-7 text-[#548B2E]" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900">Track / Raise Complaint</h2>
            <p className="text-gray-500 text-sm mt-1">Enter your Order ID and Mobile Number to continue</p>
          </div>

          <div className="space-y-4">
            <div>
              <label htmlFor="guestOrderId" className="block text-sm font-medium text-gray-700 mb-1">Order ID</label>
              <input
                id="guestOrderId"
                type="text"
                value={orderNumber}
                onChange={(e) => setOrderNumber(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleLookup()}
                placeholder="e.g., ORD-123456"
                className="w-full px-4 py-3 border border-gray-200 rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-[#548B2E]"
              />
            </div>
            <div>
              <label htmlFor="guestPhone" className="block text-sm font-medium text-gray-700 mb-1">Mobile Number</label>
              <input
                id="guestPhone"
                type="tel"
                value={guestPhone}
                onChange={(e) => setGuestPhone(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleLookup()}
                placeholder="e.g., 9876543210"
                className="w-full px-4 py-3 border border-gray-200 rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-[#548B2E]"
              />
            </div>
            {message && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-2xl text-red-700 text-sm">{message}</div>
            )}
            <button
              onClick={handleLookup}
              disabled={loading}
              className="w-full bg-[#2e4e18] text-white py-3 rounded-2xl font-semibold hover:bg-[#3f6b20] disabled:opacity-50 transition-colors"
            >
              {loading ? 'Checking…' : 'Continue'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  /* ── COMPLAINTS LIST STEP ────────────────────────────────────── */
  if (step === 'complaints') {
    return (
      <div className="min-h-screen bg-gray-50 pt-24 pb-16 px-4">
        <div className="max-w-2xl mx-auto space-y-6">
          {/* Header card */}
          <div className="bg-white rounded-3xl shadow-sm p-5 flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Your Complaints</h2>
              <p className="text-sm text-gray-500">Order: {orderNumber}</p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleManualRefresh}
                disabled={refreshing}
                title="Refresh complaints"
                className="p-2 rounded-full hover:bg-gray-100 transition-colors text-gray-500 disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              </button>
              <button
                onClick={() => { setMessage(null); setStep('create'); }}
                className="bg-[#2e4e18] text-white px-4 py-2 rounded-2xl text-sm font-semibold hover:bg-[#3f6b20] transition-colors flex items-center gap-2"
              >
                <AlertTriangle className="w-4 h-4" />
                New Complaint
              </button>
            </div>
          </div>

          {/* Complaints */}
          {complaints.length === 0 ? (
            <div className="bg-white rounded-3xl shadow-sm p-12 text-center">
              <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 mb-4">No complaints found for this order.</p>
              <button
                onClick={() => setStep('create')}
                className="text-[#548B2E] font-medium hover:underline text-sm"
              >
                Raise your first complaint
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {complaints.map((complaint) => (
                <GuestComplaintCard
                  key={complaint.id}
                  complaint={complaint}
                  orderNumber={orderNumber}
                  guestPhone={guestPhone}
                />
              ))}
            </div>
          )}

          <button
            onClick={() => setStep('lookup')}
            className="text-sm text-gray-500 hover:text-gray-700"
          >
            ← Back to Order Lookup
          </button>
        </div>
      </div>
    );
  }

  /* ── CREATE COMPLAINT STEP ───────────────────────────────────── */
  if (step === 'create') {
    const currentHint = complaintSuggestions.find((s) => s.value === issueType)?.hint;
    return (
      <div className="min-h-screen bg-gray-50 pt-24 pb-16 px-4">
        <div className="max-w-2xl mx-auto bg-white rounded-3xl shadow-sm p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900">Raise New Complaint</h2>
            <button onClick={() => { setMessage(null); setStep('complaints'); }} className="text-sm text-gray-500 hover:text-gray-700">
              ← Back
            </button>
          </div>

          <div className="space-y-5">
            <div>
              <label htmlFor="guestIssueType" className="block text-sm font-medium text-gray-700 mb-2">Issue Type</label>
              <select
                id="guestIssueType"
                value={issueType}
                onChange={(e) => setIssueType(e.target.value)}
                className="w-full px-4 py-3 border border-gray-200 rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-[#548B2E]"
              >
                {complaintSuggestions.map((s) => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
              {currentHint && <p className="text-xs text-gray-500 mt-1">{currentHint}</p>}
            </div>

            <div>
              <label htmlFor="guestDescription" className="block text-sm font-medium text-gray-700 mb-2">Description</label>
              <textarea
                id="guestDescription"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Please describe your issue in detail..."
                rows={4}
                className="w-full px-4 py-3 border border-gray-200 rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-[#548B2E]"
              />
            </div>

            <div>
              <label htmlFor="guestImages" className="block text-sm font-medium text-gray-700 mb-2">
                Attach Images
                {IMAGE_REQUIRED_TYPES.has(issueType)
                  ? <span className="ml-1 text-red-500">*</span>
                  : <span className="ml-1 text-gray-400 font-normal">(optional)</span>}
              </label>
              {IMAGE_REQUIRED_TYPES.has(issueType) && (
                <p className="text-xs text-red-500 mb-2">At least 1 image is required for this complaint type.</p>
              )}
              <input
                id="guestImages"
                type="file"
                multiple
                accept="image/*"
                onChange={handleFileChange}
                className={`w-full px-4 py-2 border rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-[#548B2E] ${
                  IMAGE_REQUIRED_TYPES.has(issueType) && images.length === 0
                    ? 'border-red-300'
                    : 'border-gray-200'
                }`}
              />
              {previews.length > 0 && (
                <div className="mt-3 grid grid-cols-4 gap-2">
                  {previews.map((src) => (
                    <img key={src} src={src} alt="preview" className="h-16 w-full object-cover rounded-xl border border-gray-200" />
                  ))}
                </div>
              )}
            </div>

            {message && (
              <div className={`p-3 rounded-2xl text-sm ${message.includes('success') ? 'bg-green-50 border border-green-200 text-green-700' : 'bg-red-50 border border-red-200 text-red-700'}`}>
                {message}
              </div>
            )}

            <button
              onClick={handleSubmitComplaint}
              disabled={submitting || !description.trim()}
              className="w-full bg-[#2e4e18] text-white py-3 rounded-2xl font-semibold hover:bg-[#3f6b20] disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
            >
              {submitting ? (
                <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Submitting…</>
              ) : (
                <><Send className="w-4 h-4" /> Submit Complaint</>
              )}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default GuestComplaints;
